import React, { useState, forwardRef } from "react";
import styled, { css } from "styled-components";
import { fallbackValues } from "./Checkbox.theme";
import { noop } from "../../../util/general";
import { SPACE_KEY } from "../../../constants/keyboard";
import Box from "../layouts/Box";
import Text from "../text";
import { themeComponent } from "../../../util/themeUtils";

const CheckboxContainer = styled.span`
  display: inline-block;
  vertical-align: middle;
`;

const CheckboxLabelContainer = styled.label`
  display: flex;
  align-items: center;
  column-gap: 1rem;
  cursor: pointer;
`;

const CheckboxIcon = styled.svg`
  fill: none;
  stroke-width: 2px;
  stroke: ${({ disabled, disabledCheckColor, checkColor }) =>
    disabled
      ? css`
          ${disabledCheckColor}
        `
      : css`
          ${checkColor}
        `};
`;

const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledCheckbox = styled.span`
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 2px;
  transition: all 150ms;

  ${CheckboxIcon} {
    visibility: ${({ checked }) => (checked ? "visible" : "hidden")};
  }

  ${({
    error,
    disabled,
    checked,
    focused,
    defaultStyles,
    checkedStyles,
    focusedStyles,
    errorStyles,
    disabledStyles,
    disabledCheckedStyles
  }) =>
    error
      ? css`
          ${errorStyles} ${focused && focusedStyles}
        `
      : disabled
      ? css`
          ${checked ? disabledCheckedStyles : disabledStyles}
        `
      : checked
      ? css`
          ${checkedStyles} ${focused && focusedStyles}
        `
      : css`
          ${defaultStyles} ${focused && focusedStyles}
        `};

  ${({ checkboxExtraStyles }) =>
    css`
      ${checkboxExtraStyles}
    `}
`;

const Checkbox = forwardRef(
  (
    {
      title,
      name,
      checked,
      onChange = noop,
      disabled = false,
      themeValues,
      hidden = false,
      error = false,
      isRequired = false,
      checkboxMargin = "0 16px 0 0",
      extraStyles,
      textExtraStyles,
      labelledById,
      dataQa = null,
      checkboxExtraStyles,
      hasIconOverride = false,
      icon: Icon,
      customAriaLabel,
      ...rest
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);

    const handleKeyDown = (e, func) => {
      if ((e.key === SPACE_KEY || e.key === "Enter") && !disabled) {
        e.preventDefault();
        func();
      }
    };

    const normalizeName = name ? name.replace(/\s+/g, "-") : "";
    const checkboxId = `checkbox-${normalizeName}`;
    const titleId = title ? `checkboxlabel-${normalizeName}` : undefined;
    const ariaLabelledById = customAriaLabel
      ? undefined
      : labelledById ?? titleId;
    const ariaLabel = ariaLabelledById ? undefined : customAriaLabel ?? name;
    const errorMessageNormalized = error
      ? `${normalizeName}-error-message`
      : undefined;

    return (
      <Box
        ref={ref}
        padding="0"
        tabIndex="0"
        role="checkbox"
        aria-checked={checked}
        aria-required={isRequired || undefined}
        aria-invalid={error}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledById}
        aria-describedby={errorMessageNormalized}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={e => {
          if (e.target === e.currentTarget && !disabled) {
            onChange();
          }
        }}
        onKeyDown={e => handleKeyDown(e, onChange)}
        hiddenStyles={hidden}
        background={themeValues.backgroundColor}
        extraStyles={`
          :focus {
            outline: 0;
          }
          ${extraStyles}; 
          margin: ${checkboxMargin};
        `}
        {...rest}
      >
        <CheckboxLabelContainer data-qa={dataQa}>
          <CheckboxContainer data-qa="Checkbox">
            <HiddenCheckbox
              id={checkboxId}
              disabled={disabled}
              name={name}
              checked={checked}
              onChange={onChange}
              tabIndex="-1"
              required={isRequired}
              aria-hidden="true"
            />
            <StyledCheckbox
              aria-hidden="true"
              error={error}
              disabled={disabled}
              checked={checked}
              focused={focused}
              defaultStyles={themeValues.defaultStyles}
              checkedStyles={themeValues.checkedStyles}
              errorStyles={themeValues.errorStyles}
              disabledStyles={themeValues.disabledStyles}
              disabledCheckedStyles={themeValues.disabledCheckedStyles}
              focusedStyles={themeValues.focusedStyles}
              checkboxExtraStyles={checkboxExtraStyles}
            >
              {hasIconOverride ? (
                <Icon />
              ) : (
                <CheckboxIcon
                  viewBox="0 0 24 24"
                  disabled={disabled}
                  disabledCheckColor={themeValues.disabledCheckColor}
                  checkColor={themeValues.checkColor}
                >
                  <polyline points="20 6 9 17 4 12" />
                </CheckboxIcon>
              )}
            </StyledCheckbox>
          </CheckboxContainer>
          {title && (
            <Text
              id={titleId}
              variant="p"
              weight={themeValues.textFontWeight}
              color={themeValues.textColor}
              extraStyles={
                textExtraStyles
                  ? `${textExtraStyles} ${disabled &&
                      `color: #6e727e; background-color: #f7f7f7;`} `
                  : `margin-left: 1rem ${disabled &&
                      `color: #6e727e; background-color: #f7f7f7;`}`
              }
            >
              {title}
            </Text>
          )}
        </CheckboxLabelContainer>
      </Box>
    );
  }
);

export default themeComponent(Checkbox, "Checkbox", fallbackValues, "default");
